/**
 * Animation Library JSON import / export orchestration (plan D23, SPEC "Animation Library JSON
 * Import / Export").
 *
 * The parsing and serialization rules live in `src/shared/librarySchema.ts`; this module only
 * adds the datastore half: what a file would do to the current library (preview) and applying
 * that decision atomically (commit).
 *
 * Import is a two-step on purpose — the user sees new / existing / identical counts and picks a
 * merge mode before anything is written. Nothing is ever written from a file with errors.
 */

import {
  AppError,
  DEFAULT_FOLDER,
  libraryToJson,
  newId,
  parseLibraryJson,
  serializeLibrary,
  type AnimationLibraryFile,
  type CanonicalAnimationClip,
} from '../shared';
import { withDb } from './db';
import {
  CLIP_FOLDERS_SETTINGS_KEY,
  FOLDERS_SETTINGS_KEY,
  readFolderState,
  sanitizeFolderState,
  withFolderWriteLock,
} from './folders';
import { clipsRepo, freeClipName } from './repos';
import { normalizeClipForStorage } from './validate';

/** How colliding clip ids are resolved. Default in the UI is `overwrite` (D23). */
export type LibraryImportMode = 'overwrite' | 'skip' | 'copy';

export const LIBRARY_IMPORT_MODES: readonly LibraryImportMode[] = ['overwrite', 'skip', 'copy'];

/** Clip ids grouped by what importing the file would do to the current library. */
export interface LibraryImportSummary {
  /** Ids not present in the library yet — always imported. */
  new: string[];
  /** Ids already present with different content — the merge mode decides. */
  existing: string[];
  /** Ids already present with byte-identical content — importing changes nothing. */
  identical: string[];
}

export type LibraryImportPreview =
  | {
      ok: true;
      file: AnimationLibraryFile;
      summary: LibraryImportSummary;
      warnings: string[];
    }
  | { ok: false; errors: string[] };

export interface LibraryImportResult {
  /** Clips that had no counterpart in the library. */
  imported: string[];
  /** Existing ids left untouched (`skip`). */
  skipped: string[];
  /** Existing ids replaced by the file's version (`overwrite`). */
  overwritten: string[];
  /** Newly generated ids for colliding clips (`copy`). */
  copied: string[];
  /**
   * Clips whose name had to be suffixed to keep library names unique. Import invents nothing the
   * user typed, so a name collision is resolved rather than rejected — but it is reported, since
   * `play("Walk")` in their game now refers to whichever clip kept the plain name.
   */
  renamed: { id: string; from: string; to: string }[];
}

/** Fixed timestamp so a fingerprint compares clip content, never export time. */
const FINGERPRINT_EXPORTED_AT = '1970-01-01T00:00:00.000Z';

/** Deterministic bytes for one clip — two clips are "identical" iff these match. */
function clipFingerprint(clip: CanonicalAnimationClip): string {
  return libraryToJson(serializeLibrary([clip], FINGERPRINT_EXPORTED_AT));
}

/**
 * The whole library as portable JSON (schema + rig version included by `serializeLibrary`), plus
 * the folder metadata — a backup that restored the clips but lost how they were organised would
 * only be half a backup.
 *
 * `overrides` is **replace-only**, and that is the one place this differs from `exportClipsJson`
 * below (audit §2.2 #1 covers all three export surfaces). There, the caller hands over an explicit
 * id list: the user pointed at those clips, so an override may also SUPPLY an id the repository has
 * never stored. Here nobody pointed at anything — "the library" is by definition the set of clips
 * that have been saved. Adding the open clip because it happens to be open would make Export →
 * Import a back-door save: the restore would write a draft the user never committed, and since
 * `newClip()` marks a clip dirty the moment it exists, every backup would start carrying whatever
 * half-authored clip was on screen. So an override replaces a record that is already in the
 * library and is otherwise ignored.
 */
export async function exportLibraryJson(
  options?: { overrides?: readonly CanonicalAnimationClip[] },
): Promise<string> {
  const [stored, folderMeta] = await Promise.all([clipsRepo.list(), readFolderState()]);
  const overrides = new Map((options?.overrides ?? []).map((clip) => [clip.id, clip]));
  // The clip on screen beats the clip on disk — for the clips that are on disk.
  const clips = overrides.size === 0 ? stored : stored.map((clip) => overrides.get(clip.id) ?? clip);
  return libraryToJson(serializeLibrary(clips, undefined, folderMeta));
}

/**
 * A named subset of the library as JSON — the per-clip / multi-clip download that ships an
 * animation to a game without shipping the character with it.
 *
 * No folder metadata: folders are how THIS editor organises the library, not something the
 * receiving project has an opinion about. Minified by default for the same reason — this file is
 * a payload, not a backup someone diffs.
 *
 * `overrides` lets a caller ship a clip it holds in memory instead of the record on disk. Clips
 * are not auto-saved, so the editor's open clip and its stored record drift apart the moment the
 * user sets a key: before this, a clip file downloaded mid-edit silently carried the last SAVED
 * shape (audit §2.2 #1, reproduced live on both export surfaces). Substitution happens here, in
 * memory — this function still writes nothing, which is what lets `libraryController` use it as a
 * pure read.
 */
export async function exportClipsJson(
  clipIds: readonly string[],
  options?: { minify?: boolean; overrides?: readonly CanonicalAnimationClip[] },
): Promise<string> {
  const wanted = [...new Set(clipIds)];
  const byId = new Map((await clipsRepo.list()).map((clip) => [clip.id, clip]));
  // The clip on screen beats the clip on disk. An override for an id that is NOT requested is
  // simply never read, and an override for an id the repository has never seen is exported all the
  // same — a clip that has only ever existed in the editor is still the clip the user asked for.
  for (const clip of options?.overrides ?? []) byId.set(clip.id, clip);

  const clips: CanonicalAnimationClip[] = [];
  const missing: string[] = [];
  for (const id of wanted) {
    const clip = byId.get(id);
    if (clip) clips.push(clip);
    else missing.push(id);
  }
  if (missing.length > 0) {
    throw new AppError(
      'CLIP_NOT_FOUND',
      missing.length === 1
        ? `No animation with id "${missing[0]}".`
        : `${missing.length} of the requested animations are no longer in the library.`,
      { missing },
    );
  }

  return libraryToJson(serializeLibrary(clips), { minify: options?.minify !== false });
}

/**
 * Parse a library file and report what importing it would do. Never writes. A file with any
 * validation error is rejected whole (`ok: false`), matching "do not silently import corrupt
 * data".
 */
export async function previewLibraryImport(text: string): Promise<LibraryImportPreview> {
  const parsed = parseLibraryJson(text);
  if (!parsed.ok) return { ok: false, errors: parsed.errors };

  const existingClips = await clipsRepo.list();
  const existingById = new Map(existingClips.map((clip) => [clip.id, clip]));

  const summary: LibraryImportSummary = { new: [], existing: [], identical: [] };
  for (const clip of parsed.file.clips) {
    const current = existingById.get(clip.id);
    if (!current) {
      summary.new.push(clip.id);
    } else if (clipFingerprint(current) === clipFingerprint(clip)) {
      summary.identical.push(clip.id);
    } else {
      summary.existing.push(clip.id);
    }
  }

  return { ok: true, file: parsed.file, summary, warnings: parsed.warnings };
}

/**
 * Apply a previously previewed file in one transaction: either every clip lands or none does.
 *
 * `createdAt` / `updatedAt` come from the file (not the clock) so an export → import → export
 * round trip is byte-stable — Scenario E compares behavior, and stable bytes make that checkable.
 */
export async function commitLibraryImport(
  file: AnimationLibraryFile,
  mode: LibraryImportMode = 'overwrite',
): Promise<LibraryImportResult> {
  if (!LIBRARY_IMPORT_MODES.includes(mode)) {
    throw new AppError('INVALID_IMPORT_MODE', `Unknown library import mode "${String(mode)}".`, {
      mode,
    });
  }
  if (!file || !Array.isArray(file.clips)) {
    throw new AppError('INVALID_LIBRARY_FILE', 'Library file has no clip list.', file);
  }

  // Validate everything before the transaction opens: a throw here writes nothing at all. Also
  // before the lock is taken, so an invalid file never delays a queued folder write.
  const incoming = file.clips.map((clip) => normalizeClipForStorage(clip));

  const apply = (): Promise<LibraryImportResult> => withDb(async (db) => {
    const result: LibraryImportResult = {
      imported: [],
      skipped: [],
      overwritten: [],
      copied: [],
      renamed: [],
    };
    // Folder metadata lands in the SAME transaction as the clips: a half-applied import that
    // wrote clips but not their folders would leave the user re-filing by hand.
    const tx = db.transaction(['clips', 'settings'], 'readwrite');
    const clipsStore = tx.objectStore('clips');
    const settingsStore = tx.objectStore('settings');
    const nameIndex = clipsStore.index('name');
    const [existingKeys, storedFolders, storedClipFolders] = await Promise.all([
      clipsStore.getAllKeys(),
      settingsStore.get(FOLDERS_SETTINGS_KEY),
      settingsStore.get(CLIP_FOLDERS_SETTINGS_KEY),
    ]);
    const existingIds = new Set(existingKeys);
    const now = Date.now();
    /** Written clip id → the id it had in the file, so a copy-mode rename carries its folder. */
    const writtenFrom = new Map<string, string>();
    const writes: Promise<unknown>[] = [];
    /** Names claimed by clips written earlier in this same transaction. */
    const claimed = new Set<string>();

    /**
     * Resolve a name that keeps the library's uniqueness invariant, recording the change. The
     * reads happen inside the transaction, so nothing else can take the name in between.
     */
    const nameFor = async (
      id: string,
      wanted: string,
    ): Promise<string> => {
      const name = await freeClipName(nameIndex, wanted, id, claimed);
      claimed.add(name);
      if (name !== wanted) result.renamed.push({ id, from: wanted, to: name });
      return name;
    };

    for (const clip of incoming) {
      if (!existingIds.has(clip.id)) {
        const stored = { ...clip, name: await nameFor(clip.id, clip.name) };
        writes.push(clipsStore.put(stored));
        existingIds.add(stored.id);
        result.imported.push(stored.id);
        writtenFrom.set(stored.id, clip.id);
        continue;
      }
      if (mode === 'skip') {
        result.skipped.push(clip.id);
        // Its name stays taken by the copy already in the library.
        claimed.add(clip.name);
        continue;
      }
      if (mode === 'overwrite') {
        const stored = { ...clip, name: await nameFor(clip.id, clip.name) };
        writes.push(clipsStore.put(stored));
        result.overwritten.push(stored.id);
        writtenFrom.set(stored.id, clip.id);
        continue;
      }
      const id = newId();
      const copy: CanonicalAnimationClip = {
        ...clip,
        id,
        name: await nameFor(id, `${clip.name} (imported)`),
        createdAt: now,
        updatedAt: now,
      };
      writes.push(clipsStore.put(copy));
      existingIds.add(copy.id);
      result.copied.push(copy.id);
      writtenFrom.set(copy.id, clip.id);
    }

    // A file that carries NEITHER folder key has no opinion about folders, and an import must not
    // invent one: game-facing clip files (`exportClipsJson`) never carry folder metadata by
    // design, so treating "no entry" as "unfiled" would empty a folder every time the user
    // restored the set file they had just exported from it. Neither settings key is touched.
    if (file.folders !== undefined || file.clipFolders !== undefined) {
      // Merge, never replace: an import adds the file's folders to the ones already here, and
      // only re-files the clips it actually wrote. A skipped clip keeps the folder the user put
      // it in.
      const current = sanitizeFolderState(storedFolders, storedClipFolders);
      const fromFile = sanitizeFolderState(file.folders, file.clipFolders);
      const fileClipFolders = fromFile.clipFolders;

      const folders = [...current.folders];
      const addFolder = (name: string): void => {
        if (name !== DEFAULT_FOLDER && !folders.includes(name)) folders.push(name);
      };
      for (const name of fromFile.folders) addFolder(name);

      const clipFolders = { ...current.clipFolders };
      for (const [writtenId, originalId] of writtenFrom) {
        const folder = fileClipFolders[originalId];
        if (folder === undefined) {
          // The file DOES describe folders and left this clip out of them, so it is saying the
          // clip is unfiled. Only then does an overwrite clear a stale assignment — otherwise
          // restoring a backup could never un-file anything.
          delete clipFolders[writtenId];
          continue;
        }
        clipFolders[writtenId] = folder;
        // The assignment is the stronger evidence: adopt a folder the file used but did not list.
        addFolder(folder);
      }

      writes.push(settingsStore.put(folders, FOLDERS_SETTINGS_KEY));
      writes.push(settingsStore.put(clipFolders, CLIP_FOLDERS_SETTINGS_KEY));
    }

    await Promise.all([...writes, tx.done]);
    return result;
  });

  // An import is a folder writer like any other (it puts both settings keys above), so it takes
  // its turn on the same chain as `moveClipToFolder` / `pruneFolderAssignments`. Without this,
  // the prune that `libraryController.refresh()` fires could read the pre-import assignment map
  // and write it straight back over the restore.
  return withFolderWriteLock(apply);
}
